import {
  ShopAndSmileAPIAdapter,
  SoftDeleteFlag,
  StoreProduct,
} from '@bofrak-backend/shared';
import { useToast } from '@chakra-ui/react';
import { liveQuery } from 'dexie';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { tShared } from '../../i18n';
import { getErrorMessage } from '../../utils/functions';
import { productDb as db } from '../db';
import {
  SyncResponseStatus,
  SyncMessageType,
  SyncResponseType,
  StoreProductSyncOptions,
  SyncStatus,
} from '../types';
import ProductSyncWorker from '../workers/product-sync-worker?worker';

const PRODUCTS_FETCH_LIMIT = 250;

export function useStoreProducts(options: StoreProductSyncOptions) {
  const {
    storeId,
    merchantId,
    apiEndpoint,
    accessToken = '',
    includeDeleted = false,
    autoSync = true,
  } = options;

  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    status: SyncResponseStatus.IDLE,
    message: '',
    error: '',
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isInitialLoadRef = useRef(isInitialLoad);
  const workerRef = useRef<Worker | null>(null);
  const subscriptionRef = useRef<any>(null);
  const toast = useToast();
  const toastIdRef = useRef<string | number | undefined>();
  const queryClient = useQueryClient();
  const lastProcessedCountRef = useRef(0);

  // Create API adapter for fetching products
  const apiAdapter = useRef<ShopAndSmileAPIAdapter | null>(null);

  useEffect(() => {
    if (apiEndpoint) {
      apiAdapter.current = new ShopAndSmileAPIAdapter({
        apiEndpoint,
        accessToken,
        retryCount: 0,
      });
    }
  }, [apiEndpoint, accessToken]);

  // Use useInfiniteQuery for fetching products with react-query caching
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery(
    ['storeProducts', storeId, merchantId, includeDeleted],
    async ({ pageParam = undefined }) => {
      if (!apiAdapter.current || !storeId || !merchantId) {
        throw new Error(tShared('Missing required parameters'));
      }
      const response = await apiAdapter.current.getStoreProducts({
        merchant_id: merchantId,
        store_id: storeId,
        cursor: pageParam ?? '',
        is_parent: true,
        include_deleted: includeDeleted
          ? SoftDeleteFlag.INCLUDE_DELETED
          : SoftDeleteFlag.EXCLUDE_DELETED,
        limit: PRODUCTS_FETCH_LIMIT,
      });

      console.log('Fetched products page', {
        pageParam,
        productsCount: response.products?.length ?? 0,
        nextCursor: response.cursor,
      });

      return {
        products: response.products || [],
        cursor: response.cursor,
      };
    },
    {
      enabled: autoSync && !!storeId && !!merchantId && !!apiEndpoint,
      getNextPageParam: (lastPage) => lastPage.cursor || undefined,
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 60 * 60 * 1000, // 1 hour
      // Real-time updates arrive over the AppSync websocket; this interval is
      // only a safety-net reconcile of the full catalog, so keep it long.
      refetchInterval: 30 * 60 * 1000,
      onError: (err) => {
        const errorMessage =
          getErrorMessage(err) || tShared('An unknown error occurred');
        setSyncStatus({
          status: SyncResponseStatus.ERROR,
          error: errorMessage,
          message: tShared('Failed to fetch products'),
        });
        toast({
          title: tShared('Sync Failed'),
          description: errorMessage,
          status: 'error',
          duration: 3000,
          position: 'bottom-right',
        });
      },
    },
  );

  // Effect to fetch all pages and send products to the worker for processing
  useEffect(() => {
    if (!data?.pages || !workerRef.current || !storeId) return;

    const allProducts = data.pages.flatMap((page) => page.products);
    const isComplete = !hasNextPage && !isFetchingNextPage;

    // Only process if we have new products or if sync is complete
    if (
      allProducts.length > 0 &&
      (allProducts.length > lastProcessedCountRef.current || isComplete)
    ) {
      // Update sync status with progress
      setSyncStatus({
        status: SyncResponseStatus.SYNCING,
        progress: allProducts.length,
        total: hasNextPage
          ? allProducts.length + PRODUCTS_FETCH_LIMIT
          : allProducts.length,
        message: tShared('Loading {{count}} products...', {
          count: allProducts.length,
        }),
      });

      // Send products to worker for IndexedDB processing
      workerRef.current.postMessage({
        type: SyncMessageType.PROCESS_PRODUCTS,
        payload: {
          storeId,
          merchantId,
          products: allProducts,
          isComplete,
          apiEndpoint,
          accessToken,
        },
      });

      lastProcessedCountRef.current = allProducts.length;
    }

    // Automatically fetch next page if available
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    storeId,
    merchantId,
    apiEndpoint,
    accessToken,
  ]);

  // Effect to show syncing toast when fetching starts
  useEffect(() => {
    if (isFetching && !isInitialLoadRef.current) {
      setSyncStatus({
        status: SyncResponseStatus.SYNCING,
        message: tShared('Refreshing products...'),
      });
    }
  }, [isFetching, toast]);

  // Initialize worker and check for cached data
  useEffect(() => {
    if (!storeId || !merchantId || !apiEndpoint) return;

    workerRef.current = new ProductSyncWorker();

    workerRef.current.onmessage = (event) => {
      const { type, payload } = event.data;

      switch (type) {
        case SyncResponseType.SYNC_COMPLETE:
          setSyncStatus({
            status: SyncResponseStatus.IDLE,
            message: payload.message,
            lastSync: payload.lastSync,
          });

          isInitialLoadRef.current = false;
          setIsInitialLoad(false);
          break;

        case SyncResponseType.SYNC_ERROR:
          setSyncStatus({
            status: SyncResponseStatus.ERROR,
            error: payload.error,
            message: payload.message,
          });

          if (toastIdRef.current) {
            toast.close(toastIdRef.current);
            toastIdRef.current = undefined;
          }

          toast({
            title: tShared('Sync Failed'),
            description: payload.error,
            status: 'error',
            duration: 3000,
            position: 'bottom-right',
          });
          break;

        case SyncResponseType.SYNC_STATUS:
          setSyncStatus({
            status: payload.status || SyncResponseStatus.IDLE,
            message: payload.message,
          });
          isInitialLoadRef.current = false;
          setIsInitialLoad(false);
          break;
      }
    };

    // Check for cached data in IndexedDB
    if (autoSync) {
      workerRef.current.postMessage({
        type: SyncMessageType.INITIAL_SYNC,
        payload: {
          storeId,
          merchantId,
          includeDeleted,
          apiEndpoint,
          accessToken,
        },
      });
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({
          type: SyncMessageType.STOP_SYNC,
          payload: { storeId, merchantId, apiEndpoint, accessToken },
        });
        workerRef.current.terminate();
        workerRef.current = null;
      }

      if (toastIdRef.current) {
        toast.close(toastIdRef.current);
      }
    };
  }, [
    storeId,
    merchantId,
    includeDeleted,
    autoSync,
    apiEndpoint,
    accessToken,
    toast,
  ]);

  // Subscribe to IndexedDB updates
  useEffect(() => {
    if (!storeId) return;

    const observable = liveQuery(async () => {
      const storeProducts = await db.getStoreProducts(storeId, includeDeleted);
      return storeProducts;
    });

    subscriptionRef.current = observable.subscribe({
      next: (storeProducts) => {
        const _storeProducts = storeProducts as StoreProduct[];

        // Sort products by popularity_score descending
        _storeProducts.sort((a, b) => {
          const scoreA = a.popularity_score || 0;
          const scoreB = b.popularity_score || 0;
          return scoreB - scoreA;
        });

        setProducts(_storeProducts);
      },
      error: (error) => {
        console.error('Error in live query:', error);
        toast({
          title: tShared('Database Error'),
          description: getErrorMessage(error),
          status: 'error',
          duration: 5000,
          position: 'bottom-right',
        });
      },
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [storeId, includeDeleted, toast]);

  const refreshProducts = useCallback(() => {
    // Reset processed count for fresh sync
    lastProcessedCountRef.current = 0;
    // Invalidate react-query cache and refetch
    queryClient.invalidateQueries([
      'storeProducts',
      storeId,
      merchantId,
      includeDeleted,
    ]);
    refetch();
  }, [queryClient, storeId, merchantId, includeDeleted, refetch]);

  const clearLocalData = useCallback(async () => {
    if (storeId) {
      await db.clearStoreData(storeId);
      setProducts([]);
      // Reset processed count
      lastProcessedCountRef.current = 0;
      // Also clear react-query cache
      queryClient.removeQueries([
        'storeProducts',
        storeId,
        merchantId,
        includeDeleted,
      ]);
      toast({
        title: tShared('Local Data Cleared'),
        description: tShared('All cached products have been removed'),
        status: 'info',
        duration: 3000,
        position: 'bottom-right',
      });
    }
  }, [storeId, merchantId, includeDeleted, queryClient, toast]);

  return {
    products,
    syncStatus,
    isInitialLoad,
    refreshProducts,
    clearLocalData,
  } as const;
}
